import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { AsyncPipe, NgIf, Location } from '@angular/common';
import moment from 'moment';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  TuiAlertService,
  TuiButtonModule,
  TuiDataListModule,
  TuiErrorModule,
  TuiLoaderModule,
  TuiTextfieldControllerModule,
  TuiSvgModule,
} from '@taiga-ui/core';
import {
  TUI_VALIDATION_ERRORS,
  TuiDataListWrapperModule,
  TuiFieldErrorPipeModule,
  TuiInputDateModule,
  TuiInputDateRangeModule,
  TuiInputModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiComboBoxModule,
  TuiStringifyContentPipeModule,
  TuiFilterByInputPipeModule,
  TuiStringifyPipeModule,
} from '@taiga-ui/kit';
import { ActivatedRoute, Router } from '@angular/router';
import { MemberService } from '../../../core/services/member.service';
import {
  TUI_DATE_FORMAT,
  TUI_DATE_SEPARATOR,
  TUI_DEFAULT_MATCHER,
  TuiBooleanHandler,
  TuiDay,
  tuiPure,
  TuiStringHandler,
} from '@taiga-ui/cdk';
import { AppUtils } from '../../../core/config/app.utils';
import { PaymentService } from '../../../core/services/payment.service';
import { AuthService } from '../../../modules/auth/services/auth.service';

@Component({
  selector: 'app-add-payment',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TuiButtonModule,
    TuiDataListWrapperModule,
    TuiErrorModule,
    TuiFieldErrorPipeModule,
    TuiInputDateModule,
    TuiInputModule,
    TuiLoaderModule,
    TuiMultiSelectModule,
    TuiTextfieldControllerModule,
    TuiInputDateRangeModule,
    TuiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    TuiSelectModule,
    TuiComboBoxModule,
    TuiDataListModule,
    TuiDataListWrapperModule,
    NgIf,
    TuiSvgModule,
    TuiStringifyContentPipeModule,
    TuiFilterByInputPipeModule,
    TuiStringifyPipeModule,
  ],
  providers: [
    PaymentService,
    MemberService,
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: {
        required: 'Required',
        email: 'Enter a valid email',
      },
    },
    { provide: TUI_DATE_FORMAT, useValue: 'YMD' },
    { provide: TUI_DATE_SEPARATOR, useValue: '/' },
  ],
  templateUrl: './add-payment.component.html',
  styleUrl: './add-payment.component.scss',
})
export class AddPaymentComponent implements OnInit {
  paymentPresets = ['1 Month', '3 Months', '6 Months', '12 Months'];
  memberId: any;
  paymentId: any;
  paymentData: any;
  paymentForm!: FormGroup;
  formErrors: any;
  isLoading = false;
  members: any;
  user: any;
  search: string | null = '';
  status = 'add';
  previous = 'view';
  customErrorMessages = {
    required: 'Amount is required.',
    pattern: 'Amount must contain numbers only.',
  };
  currentDate: any;
  dateDiff = '';
  singleMember: any;
  paymentMethodOptions: readonly string[] = ['Online', 'Cash'];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private paymentService: PaymentService,
    private memberService: MemberService,
    private readonly alerts: TuiAlertService,
    private location: Location
  ) {}

  tagValidator: TuiBooleanHandler<string> = (tag) => !tag.startsWith('Han');

  stringify: TuiStringHandler<unknown> = (item: unknown): string => {
    const typedItem = item as {
      membershipId: string;
      firstname: string;
      lastname: string;
    };
    return `${typedItem.membershipId} ${typedItem.firstname} ${typedItem.lastname}`;
  };

  ngOnInit(): void {
    this.initPaymentForm();
    this.currentDate = this.getCurrentDate();
    // Set default payment date on add
    this.paymentForm.get('date')?.patchValue(this.currentDate);
    this.route.params.subscribe((params) => {
      this.previous = params['status'];
      this.singleMember = params['user'];

      if (this.singleMember) {
        this.memberService.getOneMemberById(this.singleMember).then((res) => {
          this.memberId = res._id;
          this.paymentForm.get('memberSelected')?.patchValue(res);
          this.paymentForm.get('memberSelected')?.disable();
        });
      }
      this.paymentId = this.route.snapshot.paramMap.get('id');
      if (this.paymentId) {
        this.isLoading = true;
        this.paymentService.getOnePaymentById(this.paymentId).then(
          (res: any) => {
            this.isLoading = false;
            this.status = 'edit';
            this.paymentData = res;

            this.paymentForm.patchValue(this.paymentData);

            this.paymentForm
              .get('memberSelected')
              ?.patchValue(this.paymentData.member);
            this.paymentForm
              .get('validFrom')
              ?.patchValue(this.parseBackendDate(this.paymentData.validFrom));
            this.paymentForm
              .get('validTo')
              ?.patchValue(this.parseBackendDate(this.paymentData.validTo));
            this.paymentForm
              .get('date')
              ?.patchValue(this.parseBackendDate(this.paymentData.date));
            let methodPrefill: any = null;
            if (
              Array.isArray(this.paymentData?.paidBy) &&
              this.paymentData.paidBy.length
            ) {
              methodPrefill = this.paymentData.paidBy[0]; // 'Online' | 'Cash'
            }
            this.paymentForm.get('paymentMethod')?.patchValue(methodPrefill);
            this.paymentForm.get('memberSelected')?.disable();
          },
          (e: any) => {
            this.isLoading = false;
          }
        );
      }
    });
    this.user = this.authService.getLoggedInUser();
    this.memberService.getAllMembers().then((res) => {
      this.members = res;
      if (this.singleMember) {
        const member = this.members.find(
          (member: any) => member.membershipId === this.singleMember
        );
        this.paymentForm
          .get('member')
          ?.patchValue(
            `${member.membershipId} ${member.firstname} ${member.lastname}`
          );
      }
    });
  }

  parseBackendDate(dateString: string): TuiDay {
    const date = new Date(dateString);
    return new TuiDay(date.getFullYear(), date.getMonth(), date.getDate());
  }

  initPaymentForm(): void {
    this.paymentForm = this.formBuilder.group({
      date: [null, Validators.compose([Validators.required])],
      validFrom: [null, Validators.compose([Validators.required])],
      validTo: [null, Validators.compose([Validators.required])],
      // validTo: [{ value: null, disabled: true }],
      memberSelected: [null, Validators.compose([Validators.required])],
      amount: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^\d+(\.\d{1,2})?$/), // Allow decimal numbers with up to 2 decimal places
        ]),
      ],
      paymentPreset: [null],
      handledBy: [null],
      paymentMethod: [null, Validators.compose([Validators.required])],
      notes: [null],
    });
    // @ts-ignore
    this.paymentForm
      .get('paymentPreset')
      .valueChanges.subscribe((preset: string) => {
        if (preset) {
          const dateControl = this.paymentForm.get('date');
          if (dateControl && dateControl.value) {
            const date = dateControl.value;
            this.paymentForm.patchValue({
              validFrom: date,
              validTo: this.calculateValidToDate(
                this.tuiDateToDate(date),
                preset
              ),
            });
          }
        }
      });
    // @ts-ignore
    this.paymentForm
      .get('validFrom')
      .valueChanges.subscribe((validFrom: any) => {
        // @ts-ignore
        const preset = this.paymentForm.get('paymentPreset').value;
        if (validFrom && preset) {
          this.paymentForm.patchValue({
            validTo: this.calculateValidToDate(
              this.tuiDateToDate(validFrom),
              preset
            ),
          });
        }
      });

    this.paymentForm.valueChanges.subscribe((change: any) => {
      if (change && change.validFrom && change.validTo) {
        const date1 = moment(change.validTo);
        const date2 = moment(change.validFrom);

        const diffInMonths = date1.diff(date2, 'months');
        date2.add(diffInMonths, 'months');
        const diffInDays = date1.diff(date2, 'days');

        this.dateDiff =
          diffInMonths === 1
            ? `${diffInMonths} month ${diffInDays} days`
            : `${diffInMonths} months ${diffInDays} days`;
      }
    });

    this.paymentForm.valueChanges.subscribe(() => {
      this.formErrors = AppUtils.getFormErrors(
        this.paymentForm,
        this.formErrors
      );
    });
  }

  tuiDateToDate(date: any): any {
    if (!date) {
      return null;
    }

    const year = date.year;
    const month = date.month;
    const day = date.day;

    const utcDate = new Date(Date.UTC(year, month, day));

    return utcDate;
  }

  calculateValidToDate(startDate: Date, preset: string): any {
    const endDate = new Date(startDate);
    switch (preset) {
      case '1 Month':
        endDate.setMonth(startDate.getMonth() + 1);
        break;
      case '3 Months':
        endDate.setMonth(startDate.getMonth() + 3);
        break;
      case '6 Months':
        endDate.setMonth(startDate.getMonth() + 6);
        break;
      case '12 Months':
        endDate.setMonth(startDate.getMonth() + 12);
        break;
      default:
        break;
    }
    return this.parseBackendDate(endDate.toISOString());
  }

  submit(): void {
    if (this.paymentForm.valid) {
      // Get the selected member
      const selectedMember = this.paymentForm.get('memberSelected')?.value;
      console.log('Selected member:', selectedMember);

      if (!selectedMember) {
        this.alerts
          .open('<p style="color: red">Please select a member</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      // Validate required fields
      const amount = this.paymentForm.get('amount')?.value;
      const paymentPreset = this.paymentForm.get('paymentPreset')?.value;
      const paymentMethod = this.paymentForm.get('paymentMethod')?.value;

      if (!amount) {
        this.alerts
          .open('<p style="color: red">Please enter an amount</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      if (!paymentPreset) {
        this.alerts
          .open('<p style="color: red">Please select a payment preset</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }
      if (!paymentMethod) {
        this.alerts
          .open('<p style="color: red">Please select a payment method</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      // Prepare the payload with proper data structure
      const dateValue = this.tuiDateToDate(this.paymentForm.get('date')?.value);
      const validFromValue = this.tuiDateToDate(
        this.paymentForm.get('validFrom')?.value
      );
      const validToValue = this.tuiDateToDate(
        this.paymentForm.get('validTo')?.value
      );

      if (!dateValue || !validFromValue || !validToValue) {
        this.alerts
          .open('<p style="color: red">Please fill in all date fields</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      const paidBy = Array.isArray(paymentMethod)
        ? paymentMethod
        : [paymentMethod];

      console.log(
        'Before Payload Creation:',
        this.paymentForm.get('paymentMethod')?.value
      );

      const payload = {
        member: [selectedMember._id],
        amount: this.paymentForm.get('amount')?.value,
        paymentPreset: this.paymentForm.get('paymentPreset')?.value,
        handledBy: this.user.userId,
        paidBy: paidBy.filter(Boolean),
        notes: this.paymentForm.get('notes')?.value,
        // Convert TuiDay objects to ISO date strings
        date: dateValue.toISOString(),
        validFrom: validFromValue.toISOString(),
        validTo: validToValue.toISOString(),
      };

      console.log('Final payload before API call:', payload);

      this.isLoading = true;

      this.paymentService
        .createPayment(payload)
        .then((response) => {
          this.isLoading = false;
          this.alerts
            .open('<p style="color: green">Payment Recorded Successfully</p>')
            .subscribe();
          // Mark member payment status as Paid
          this.memberService
            .updateMember({
              _id: selectedMember._id,
              paymentStatus: { type: 'Paid' },
            })
            .catch(() => {});
          if (this.previous === 'single') {
            this.navigateToSingleViewMember();
          } else {
            this.navigateToView();
          }
        })
        .catch((e) => {
          console.log('Payment creation error:', e);
          console.log('Error details:', JSON.stringify(e, null, 2));
          this.isLoading = false;
          this.alerts
            .open('<p style="color: red">Error Creating Payment Record</p>', {
              status: 'error',
            })
            .subscribe();
        });
    } else {
      console.log('Form is invalid');
      console.log('Form errors:', this.paymentForm.errors);
      console.log('Form status:', this.paymentForm.status);
      // Mark all fields as touched to display validation errors
      Object.keys(this.paymentForm.controls).forEach((key) => {
        const control = this.paymentForm.get(key);
        console.log(
          `Control: ${key}, Status: ${control?.status}, Value:`,
          control?.value,
          'Errors:',
          control?.errors
        );
      });
      this.paymentForm.markAllAsTouched();
    }
  }

  onUpdate(): void {
    if (this.paymentForm.valid) {
      this.isLoading = true;

      // Get the selected member
      const selectedMember = this.paymentForm.get('memberSelected')?.value;

      if (!selectedMember) {
        this.alerts
          .open('<p style="color: red">Please select a member</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      // Validate required fields
      const amount = this.paymentForm.get('amount')?.value;
      const paymentPreset = this.paymentForm.get('paymentPreset')?.value;
      const paymentMethod = this.paymentForm.get('paymentMethod')?.value;

      if (!amount) {
        this.alerts
          .open('<p style="color: red">Please enter an amount</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      if (!paymentPreset) {
        this.alerts
          .open('<p style="color: red">Please select a payment preset</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }
      if (!paymentMethod) {
        this.alerts
          .open('<p style="color: red">Please select a payment method</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      // Prepare the update payload with proper data structure
      const dateValue = this.tuiDateToDate(this.paymentForm.get('date')?.value);
      const validFromValue = this.tuiDateToDate(
        this.paymentForm.get('validFrom')?.value
      );
      const validToValue = this.tuiDateToDate(
        this.paymentForm.get('validTo')?.value
      );

      if (!dateValue || !validFromValue || !validToValue) {
        this.alerts
          .open('<p style="color: red">Please fill in all date fields</p>', {
            status: 'error',
          })
          .subscribe();
        return;
      }

      const payload = {
        _id: this.paymentId,
        member: [selectedMember._id], // Backend expects array of member IDs
        amount: this.paymentForm.get('amount')?.value,
        paymentPreset: this.paymentForm.get('paymentPreset')?.value,
        handledBy: this.user.userId,
        paidBy: [paymentMethod],
        notes: this.paymentForm.get('notes')?.value,
        // Convert TuiDay objects to ISO date strings
        date: dateValue.toISOString(),
        validFrom: validFromValue.toISOString(),
        validTo: validToValue.toISOString(),
      };

      console.log('Sending update payload:', payload);

      this.paymentService
        .updatePayment(payload)
        .then(() => {
          this.isLoading = false;
          this.alerts
            .open('<p style="color: green">Payment Updated Successfully</p>')
            .subscribe();
          if (this.previous === 'single') {
            this.navigateToSingleViewMember();
          } else {
            this.navigateToView();
          }
        })
        .catch((e) => {
          console.log('Payment update error:', e);
          this.isLoading = false;
          this.alerts
            .open('<p style="color: red">Error Updating Payment</p>', {
              status: 'error',
            })
            .subscribe();
        });
    } else {
      // Mark all fields as touched to display validation errors
      this.paymentForm.markAllAsTouched();
    }
  }

  getCurrentDate(): any {
    const date = new Date();
    return new TuiDay(date.getFullYear(), date.getMonth(), date.getDate());
  }

  cancel(): void {
    if (this.previous === 'single') {
      this.navigateToSingleViewMember();
    } else {
      this.navigateToView();
    }
  }

  navigateToView(): void {
    // this.location.back();
    this.router.navigate(['admin/payments']);
  }

  navigateToSingleViewMember(): void {
    if (this.memberId) {
      this.router.navigate([`admin/view-member/${this.memberId}`, { tab: 1 }]);
    } else {
      // @ts-ignore
      this.router.navigate([
        `admin/view-member/${this.paymentData.member._id}`,
        { tab: 1 },
      ]);
    }
  }
}
